<template>
  <div class="page-container studio-page">
    <Header dark />
    <template v-if="game">
      <section class="studio-banner hero">
        <router-link class="back" :to="{ name: 'FluorineRepublika', hash: '#' + game.slug }">← Fluorine Republika</router-link>
        <div class="hero-row">
          <div class="game-card-image hero-image">
            <img v-if="game.image" :src="game.image" :alt="game.name" :class="game.imageFit">
            <span v-else class="game-card-initial">{{ (game.shortName || game.name).charAt(0) }}</span>
          </div>
          <div>
            <span class="badge">{{ game.status }} · <PlatformList :platforms="game.platforms" /></span>
            <h1 class="studio-title">{{ game.name }} <span v-if="game.codename" class="working-title" title="Working title. The real name comes at launch.">Codename</span></h1>
            <p class="lead">{{ game.pitch }}</p>
            <div class="how-you-pay">
              <span class="how-you-pay-label">How you pay</span>
              <p>{{ game.howYouPay }}</p>
            </div>
            <div class="buttons">
              <template v-for="link in game.links" :key="link.label">
                <a v-if="!link.soon" class="studio-button" :href="link.url" target="_blank" rel="noopener">{{ link.label }}</a>
                <span v-else class="studio-button soon" title="Coming soon">{{ link.label }} (soon)</span>
              </template>
            </div>
          </div>
        </div>
      </section>

      <div v-if="game.features.length" class="page-element">
        <h2 class="titles">The game</h2>
        <div class="cards">
          <div v-for="item in game.features" :key="item.title" class="card">
            <h3 class="subtitle">{{ item.title }}</h3>
            <p class="desc">{{ item.text }}</p>
          </div>
        </div>
      </div>

      <div v-if="game.gallery.length" class="page-element">
        <h2 class="titles">Screenshots</h2>
        <div class="gallery">
          <img v-for="(src, i) in game.gallery" :key="i" :src="src" :alt="game.name + ' screenshot ' + (i + 1)" loading="lazy">
        </div>
      </div>

      <div class="page-element">
        <h2 class="titles">{{ game.status === 'Released' || game.status === 'Early access' ? 'What it taught me' : 'What it\'s teaching me' }}</h2>
        <p class="desc wide">{{ game.taught }}</p>
      </div>

      <div v-if="game.lore" class="page-element">
        <h2 class="titles">Lore</h2>
        <p class="desc wide">{{ game.lore }}</p>
      </div>

      <div v-for="block in blocks" :key="block.title" class="page-element">
        <h2 class="titles">{{ block.title }}</h2>
        <div class="cards">
          <div v-for="item in block.items" :key="item.title" class="card">
            <h3 class="subtitle">{{ item.title }}</h3>
            <p class="desc">{{ item.text }}</p>
          </div>
        </div>
      </div>

      <component v-for="(story, i) in stories" :key="i" :is="story.component" :story="story" />

      <div v-if="!hasDeepDive" class="page-element">
        <h2 class="titles">Coming soon</h2>
        <p class="desc wide">The full story of this game (development, UX decisions and what I would do differently) is being written.</p>
      </div>

      <div v-if="game.roadmap" class="page-element">
        <h2 class="titles">Roadmap</h2>
        <div class="cards">
          <div class="card">
            <h3 class="subtitle">In the game now</h3>
            <ul class="desc"><li v-for="item in game.roadmap.now" :key="item">{{ item }}</li></ul>
          </div>
          <div class="card">
            <h3 class="subtitle">Next up</h3>
            <ul class="desc"><li v-for="item in game.roadmap.next" :key="item">{{ item }}</li></ul>
          </div>
          <div class="card">
            <h3 class="subtitle">Later</h3>
            <ul class="desc"><li v-for="item in game.roadmap.later" :key="item">{{ item }}</li></ul>
          </div>
        </div>
      </div>

      <p v-if="game.privacy" class="legal">
        <a :href="game.privacy" target="_blank" rel="noopener">Privacy notice</a>
      </p>
    </template>

    <div v-else class="page-element">
      <h2 class="titles">Game not found</h2>
      <router-link :to="{ name: 'FluorineRepublika' }">Back to Fluorine Republika</router-link>
    </div>
    <SiteFooter dark />
  </div>
</template>

<script>
import Header from './header.vue';
import SiteFooter from '@/components/SiteFooter.vue';
import PlatformList from '@/components/PlatformList.vue';
import { findGame } from '@/data/games';

export default {
  name: 'GamePage',
  components: { Header, SiteFooter, PlatformList },
  computed: {
    game() {
      return findGame(this.$route.params.slug);
    },
    blocks() {
      if (!this.game) return [];
      return [
        { title: 'Development', items: this.game.development },
        { title: 'UX problems I solved', items: this.game.ux },
        { title: 'Economy and balance', items: this.game.economy }
      ].filter((block) => block.items.length);
    },
    // Optional per-game deep dive (games.js `story`), e.g. Desk Pond's infographic. A list so pages without one render nothing.
    stories() {
      return this.game && this.game.story ? [this.game.story] : [];
    },
    hasDeepDive() {
      return this.blocks.length > 0 || !!this.game.lore || !!this.game.story;
    }
  }
}
</script>

<style lang="scss" scoped>
@use '../assets/styles/global.scss' as *;
@use '../assets/styles/studio.scss' as *;

.hero {
  text-align: left;
}
.page-element {
  box-sizing: border-box;
}
.back {
  display: inline-block;
  margin-bottom: 16px;
  color: var(--fluorine);
  text-decoration: none;
}
.hero-row {
  display: flex;
  gap: 32px;
  align-items: center;
}
.hero-image {
  flex: 0 0 220px;
  height: 260px;
}
.lead {
  max-width: 640px;
  font-family: "PT Sans Narrow";
  font-size: 18px;
}
.buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
}
.cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 24px;
  width: 100%;
  margin-bottom: 30px;
}
.card .desc {
  width: auto;
}
.desc.wide {
  width: auto;
  max-width: 760px;
  margin-bottom: 30px;
}
ul.desc {
  padding-left: 20px;
}
.gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 30px;
  img {
    height: 420px;
    max-width: 100%;
    border-radius: 12px;
    object-fit: contain;
  }
}
.legal {
  font-size: 14px;
  a {
    color: var(--grayblue);
  }
}
@media (max-width: 768px) {
  .hero-row {
    flex-direction: column;
    align-items: flex-start;
  }
  .hero-image {
    flex: none;
    width: 100%;
  }
  .page-element {
    box-sizing: border-box;
    width: 100%;
    padding: 0 16px;
  }
  .gallery img {
    height: auto;
    width: 100%;
  }
}
</style>
